import { useParams } from "react-router";
import { skipToken } from "@reduxjs/toolkit/query";

import {
  selectCurrentAccount,
  selectCurrentAllocation,
  selectCurrentSubscription,
  selectFolderById,
  useGetAccountModulesQuery,
  useGetAccountSubscriptionsQuery,
} from "fond/api";
import { SubscriptionStatus } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { accountModuleCheck, Actions, licenseCheck, permissionCheck, subscriptionCheck } from "fond/utils/permissions";

interface RouteParams {
  folderId: string;
}

type CreatePermissionCheckResult = {
  canAddFolder: boolean;
  canCreateProject: boolean;
  canImportReport: boolean;
  canWriteCityPlannerProject: boolean;
  canWriteISGDesignProjects: boolean;
  canWritePlannerProjects: boolean;
  canWritePlannerExtendedProjects: boolean;
  canWriteNBNProjects: boolean;
  canWriteMuPowerProjects: boolean;
  hasActiveSubscription: boolean;
  hasNBNModule: boolean;
  hasMuPowerModule: boolean;
  hasValidLicense: boolean;
};

/**
 * Checks whether the user has permission to create items within the current account and folder context.
 */
export const useCreatePermissionCheck = (): CreatePermissionCheckResult => {
  const { folderId } = useParams<keyof RouteParams>();
  const currentAllocation = useAppSelector(selectCurrentAllocation);
  const currentAccount = useAppSelector(selectCurrentAccount);

  const folder = useAppSelector((state) => (folderId ? selectFolderById(state, folderId) : undefined));
  const { data: subscriptions } = useGetAccountSubscriptionsQuery(currentAllocation?.Account.ID ?? skipToken);
  const hasActiveSubscription = Boolean(subscriptions?.some((subscription) => subscription.Status === SubscriptionStatus.Active));
  const hasValidLicense = licenseCheck(currentAllocation, Actions.PROJECT_ADD) && hasActiveSubscription;
  const canCreateFolder = licenseCheck(currentAllocation, Actions.FOLDER_ADD) && hasActiveSubscription;
  const canAddFolder = (!folder || permissionCheck(folder.Permission.Level, Actions.FOLDER_ADD)) && canCreateFolder;
  const canCreateProject = (!folder || permissionCheck(folder.Permission.Level, Actions.PROJECT_ADD)) && hasValidLicense;

  const { data: accountModules } = useGetAccountModulesQuery(currentAccount?.ID ?? skipToken);
  const canWritePlannerProjectsResult = useAppSelector(
    (state) =>
      currentAccount &&
      hasActiveSubscription &&
      licenseCheck(selectCurrentAllocation(state), Actions.PLANNER_PROJECT_ADD) &&
      subscriptionCheck(selectCurrentSubscription(state, currentAccount.ID), Actions.PLANNER_PROJECT_ADD)
  );
  const canWritePlannerProjects = Boolean(canWritePlannerProjectsResult);
  const canImportReport = accountModuleCheck(accountModules, Actions.REPORT_IMPORT);
  const hasNBNModule = accountModuleCheck(accountModules, Actions.NBN_DESIGN_ADD);
  const hasISGModule = accountModuleCheck(accountModules, Actions.ISG_DESIGN_ADD);
  const hasCityPlannerModule = accountModuleCheck(accountModules, Actions.CITY_PLANNER_ADD);
  const hasCityPlannerCreatePermission = !folder || permissionCheck(folder.Permission.Level, Actions.CITY_PLANNER_ADD);
  const canCreateCityPlannerProject = hasCityPlannerCreatePermission && hasCityPlannerModule && hasValidLicense;
  const writeCityPlannerProjectPermission =
    useAppSelector(
      (state) =>
        currentAccount &&
        hasActiveSubscription &&
        licenseCheck(selectCurrentAllocation(state), Actions.CITY_PLANNER_ADD) &&
        subscriptionCheck(selectCurrentSubscription(state, currentAccount.ID), Actions.CITY_PLANNER_ADD)
    ) ?? undefined;
  const canWriteCityPlannerProject = Boolean(writeCityPlannerProjectPermission && canCreateCityPlannerProject);

  // Planner Extended
  const canWritePlannerExtendedProjects = Boolean(
    useAppSelector(
      (state) =>
        currentAccount &&
        hasValidLicense &&
        canCreateProject &&
        subscriptionCheck(selectCurrentSubscription(state, currentAccount.ID), Actions.PLANNER_EXTENDED_ADD, "Design")
    ) ?? undefined
  );

  // ISG Design Projects
  const canWriteISGDesignProjects = Boolean(
    useAppSelector(
      (state) =>
        hasISGModule &&
        currentAccount &&
        hasActiveSubscription &&
        canCreateProject &&
        subscriptionCheck(selectCurrentSubscription(state, currentAccount.ID), Actions.ISG_DESIGN_ADD, "Design")
    ) ?? undefined
  );

  /**
   * NBN Projects require the following:
   * - NBN module
   * - Valid license
   * - Active subscription (no planner module needed)
   * - Create project permission
   */
  const canWriteNBNProjects = Boolean(currentAccount && hasActiveSubscription && hasNBNModule && canCreateProject);

  /**
   * MU_POWER projects require:
   * - MU_POWER module
   * - Valid license
   * - Active subscription
   * - Create project permission
   */
  const hasMuPowerModule = accountModuleCheck(accountModules, Actions.MU_POWER_DESIGN_ADD);
  const canWriteMuPowerProjects = Boolean(currentAccount && hasActiveSubscription && hasMuPowerModule && canCreateProject);

  return {
    canAddFolder,
    canCreateProject,
    canImportReport,
    canWriteCityPlannerProject,
    canWritePlannerProjects,
    canWritePlannerExtendedProjects,
    canWriteISGDesignProjects,
    canWriteNBNProjects,
    canWriteMuPowerProjects,
    hasActiveSubscription,
    hasNBNModule,
    hasMuPowerModule,
    hasValidLicense,
  };
};
